import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Editor } from "components/common/common";
import s from "./ArticleForUser.module.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchArticle } from "store/articlesSlice";
import { Eye } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { LineSpinner } from "ldrs/react";
import "ldrs/react/LineSpinner.css";

const ArticleForUser = () => {
  const { hash } = useParams();
  const dispatch = useDispatch();
  const userSide = true;
  const { currentArticle, loading: isLoading, error } = useSelector((state) => state.articles);
  const [showLoading, setShowLoading] = useState(true);

  // Добавляем таймаут для защиты от вечной загрузки
  const initialNavigationDone = useRef(false);
  const loadingTimeoutRef = useRef(null);

  // Функция для безопасного скрытия загрузки
  const safeHideLoading = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    setShowLoading(false);
    initialNavigationDone.current = true;
  }, []);

  // Таймаут на случай, если что-то пойдет не так
  useEffect(() => {
    loadingTimeoutRef.current = setTimeout(() => {
      console.warn("Loading timeout reached - forcing hide");
      safeHideLoading();
    }, 10000); // 10 секунд максимум

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [safeHideLoading]);

  // Навигация к закладке с ожиданием полной загрузки всех изображений
  const navigateToBookmark = useCallback(
    (bookmarkId) => {
      if (!bookmarkId || initialNavigationDone.current) return;

      console.log("Navigating to bookmark:", bookmarkId);

      const checkAndScroll = () => {
        // Проверяем загрузку всех изображений в статье
        const images = document.querySelectorAll("article img, .ce-block img");
        const allImagesLoaded = Array.from(images).every((img) => img.complete && img.naturalHeight !== 0);
        
        if (!allImagesLoaded && images.length > 0) {
          let loadedCount = 0;
          const totalImages = images.length;

          const onImageLoad = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
              scrollToBookmark();
            }
          };

          images.forEach((img) => {
            if (img.complete && img.naturalHeight !== 0) {
              loadedCount++;
            } else {
              img.addEventListener("load", onImageLoad, { once: true });
              img.addEventListener("error", onImageLoad, { once: true });
            }
          });

          if (loadedCount === totalImages) {
            scrollToBookmark();
          }
        } else {
          scrollToBookmark();
        }
      };

      const scrollToBookmark = () => {
        const element = document.getElementById(bookmarkId);
        if (element) {
          const elementRect = element.getBoundingClientRect();
          const elementTop = elementRect.top + window.pageYOffset;
          const targetPosition = elementTop;

          window.scrollTo({
            top: targetPosition,
            behavior: "smooth",
          });
          
          setTimeout(() => {
            safeHideLoading();
          }, 500);
        } else {
          safeHideLoading();
        }
      };

      setTimeout(checkAndScroll, 100);
    },
    [safeHideLoading]
  );

  // Обработка загрузки статьи
  useEffect(() => {
    if (hash) {
      dispatch(fetchArticle({ hash, userSide }));
    }
  }, [hash, dispatch, userSide]);

  // Обработка успешной загрузки статьи
  useEffect(() => {
    if (currentArticle && !isLoading) {
      console.log("Article loaded, starting navigation process");

      const urlParams = new URLSearchParams(window.location.search);
      const bookmarkId = urlParams.get("bookmark") || window.location.hash.substring(1);

      if (bookmarkId) {
        navigateToBookmark(bookmarkId);
      } else {
        // Если закладки нет, скрываем загрузку сразу
        safeHideLoading();
      }
    }
  }, [currentArticle, isLoading, navigateToBookmark, safeHideLoading]);

  // Обработка ошибок
  useEffect(() => {
    if (error) {
      console.error("Article loading error:", error);
      safeHideLoading();
    }
  }, [error, safeHideLoading]);

  // Дополнительная защита - скрытие загрузки при любом изменении состояния
  useEffect(() => {
    if (!isLoading && currentArticle) {
      const timer = setTimeout(() => {
        safeHideLoading();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isLoading, currentArticle, safeHideLoading]);

  // Рендер контента
  return (
    <div>
      <Helmet prioritizeSeoTags>
        <title>{currentArticle?.meta?.title || "D-KEYS app for intercom"}</title>
        <meta
          name="description"
          content={currentArticle?.meta?.userDescription || currentArticle?.meta?.shortDescription || "D-KEYS app for intercom"}
        />
      </Helmet>

      {/* Основной контент */}
      <div className={s.content}>
        <div className={s.container}>
          <div className={s.views}>
            <div className={s.viewsicon}>
              <Eye
                size="14"
                color="#959393"
              />
            </div>
            <div>{currentArticle?.meta?.views || 0}</div>
          </div>
          <div className={s.header}>
            <h1>{currentArticle?.meta?.title}</h1>
          </div>

          {currentArticle && (
            <Editor
              data={currentArticle.content}
              readOnly={true}
              holder={`viewer-${hash}`}
              key={`editor-${hash}`}
              viewMode="articleForUser"
            />
          )}
        </div>
      </div>

      {/* Окно загрузки */}
      {showLoading && (
        <div className={s.loadingOverlay}>
          <div className={s.loadingContent}>
            <LineSpinner
              size="40"
              stroke="3"
              speed="1"
              color="black"
            />
            <p className={s.loadingText}>{error ? "Ошибка загрузки" : "Загрузка статьи..."}</p>
            {/* Добавляем кнопку для принудительного скрытия */}
            {error && (
              <button
                onClick={safeHideLoading}
                className={s.forceHideButton}
              >
                Продолжить
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleForUser;
